import type { TimesheetEntry, Timesheet } from '@/types/timesheet';
import type { AnomalySeverity } from '@/types/anomaly';

export interface EntryVariance {
  scheduled: number;
  actual: number;
  variance: number;
  percentage: number;
  severity: AnomalySeverity;
}

export interface TimesheetVarianceSummary {
  totalScheduledBookings: number;
  dismissedCount: number;
  highVarianceCount: number;
  mediumVarianceCount: number;
  lowVarianceCount: number;
  totalScheduledHours: number;
  totalLoggedHours: number;
  totalVarianceHours: number;
  totalVariancePercentage: number;
  hasSignificantVariances: boolean;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

/**
 * Calculate variance for a single timesheet entry
 */
export function calculateEntryVariance(entry: TimesheetEntry): EntryVariance | null {
  if (!entry.isFromSchedule || !entry.scheduledHours) {
    return null;
  }

  const scheduled = DAYS.reduce((sum, day) => sum + entry.scheduledHours![day], 0);
  const actual = DAYS.reduce((sum, day) => sum + entry.hours[day], 0);
  const variance = actual - scheduled;
  const percentage = scheduled > 0 ? Math.round((variance / scheduled) * 100) : 0;

  return {
    scheduled,
    actual,
    variance,
    percentage,
    severity: getSeverityFromPercentage(Math.abs(percentage)),
  };
}

/**
 * Calculate variance summary for an entire timesheet
 */
export function calculateTimesheetVarianceSummary(timesheet: Timesheet): TimesheetVarianceSummary | null {
  const scheduledEntries = timesheet.entries.filter(e => e.isFromSchedule);

  if (scheduledEntries.length === 0) {
    return null;
  }

  const variances = scheduledEntries
    .map(calculateEntryVariance)
    .filter((v): v is EntryVariance => v !== null);

  const dismissedCount = scheduledEntries.filter(e => e.isDismissed).length;
  const highVarianceCount = variances.filter(v => v.severity === 'high').length;
  const mediumVarianceCount = variances.filter(v => v.severity === 'medium').length;
  const lowVarianceCount = variances.filter(v => v.severity === 'low').length;

  const totalScheduledHours = variances.reduce((sum, v) => sum + v.scheduled, 0);
  const totalLoggedHours = variances.reduce((sum, v) => sum + v.actual, 0);
  const totalVarianceHours = totalLoggedHours - totalScheduledHours;
  const totalVariancePercentage = totalScheduledHours > 0 
    ? Math.round((totalVarianceHours / totalScheduledHours) * 100) 
    : 0;

  const hasSignificantVariances = 
    Math.abs(totalVariancePercentage) > 10 || 
    dismissedCount > 0 || 
    highVarianceCount > 0;

  return {
    totalScheduledBookings: scheduledEntries.length,
    dismissedCount,
    highVarianceCount,
    mediumVarianceCount,
    lowVarianceCount,
    totalScheduledHours,
    totalLoggedHours,
    totalVarianceHours,
    totalVariancePercentage,
    hasSignificantVariances,
  };
}

/**
 * Map variance percentage to severity level
 */
export function getSeverityFromPercentage(absPercentage: number): AnomalySeverity {
  if (absPercentage <= 10) return 'low';
  if (absPercentage <= 25) return 'medium';
  return 'high';
}

/**
 * Get variance badge variant based on severity
 */
export function getVarianceBadgeVariant(severity: AnomalySeverity): 'destructive' | 'default' | 'secondary' {
  switch (severity) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
  }
}

/**
 * Format variance for display
 */
export function formatVariance(variance: number, percentage: number): string {
  const sign = variance > 0 ? '+' : '';
  return `${sign}${variance}h (${sign}${percentage}%)`;
}

/**
 * Get variance description for display
 */
export function getVarianceDescription(variance: EntryVariance): string {
  if (variance.variance === 0) {
    return 'On track - logged hours match schedule';
  }
  
  if (variance.variance > 0) {
    return `Over schedule by ${variance.variance}h (${variance.percentage}% more than planned)`;
  }
  
  return `Under schedule by ${Math.abs(variance.variance)}h (${Math.abs(variance.percentage)}% less than planned)`;
}
